// Local CV parser — regex + keyword dictionaries only.
//
// No Claude, no /api calls, no network requests beyond the PDF
// extraction that happens inside pdfPosExtract. Everything runs in
// the browser. Free, offline, deterministic.
//
// Pipeline per CV:
//   1. extractCVText(file)       — pdfjs-dist, text only
//   2. analyseCV(text)            — regex + dictionary matches
//   3. scoreCandidate(extracted, jobOpening, branch) — rule-based
//   4. saveCandidate(...)         — recruitmentStore
//
// processCVBatch orchestrates all four, fires progress callbacks,
// skips duplicates, and keeps going on individual failures.

import * as Sentry from '@sentry/react'
import { extractPdfText } from './pdfPosExtract'
import {
  findExistingCandidate, saveCandidate, getCandidates, updateCandidate,
  BRANCH_LOCAL_LANGUAGES,
} from './recruitmentStore'

const MAX_CV_BYTES = 5 * 1024 * 1024
const DEFAULT_BATCH_DELAY_MS = 200 // tiny yield so the UI can paint
const MAX_BATCH_SIZE = 20

// ─── 1. Skills dictionary ────────────────────────────────────────

export const SKILLS_DICTIONARY = {
  food_service: [
    'food preparation', 'food safety', 'food handling', 'haccp',
    'kitchen management', 'cooking', 'cook', 'chef', 'baking',
    'catering', 'menu planning', 'stock control', 'inventory management',
    'food hygiene', 'hygiene', 'cleaning', 'dishwashing',
    'serving', 'waitering', 'waitressing', 'waiter', 'waitress',
    'cashiering', 'till operation', 'cash handling',
    'customer service', 'barista', 'coffee', 'butchery', 'grilling',
  ],
  management: [
    'management', 'supervisor', 'team leader', 'leadership',
    'staff management', 'scheduling', 'budgeting', 'reporting',
    'training', 'mentoring', 'procurement', 'operations',
  ],
  admin: [
    'microsoft office', 'excel', 'word', 'outlook', 'typing',
    'data entry', 'filing', 'administration', 'reception',
    'bookkeeping', 'accounting', 'payroll', 'invoicing',
  ],
  languages: [
    'english', 'afrikaans', 'zulu', 'isizulu', 'xhosa', 'isixhosa',
    'sotho', 'sesotho', 'tswana', 'setswana', 'tshivenda', 'venda',
    'tsonga', 'xitsonga', 'ndebele', 'isindebele', 'swati', 'siswati',
    'sepedi', 'northern sotho', 'pedi',
  ],
  qualifications: [
    'matric', 'grade 12', 'diploma', 'degree', 'certificate',
    'national certificate', 'higher certificate', 'honours',
    'bachelors', 'bachelor', 'btech', 'masters', 'phd', 'doctorate',
    'nvq', 'city and guilds',
  ],
  soft_skills: [
    'communication', 'teamwork', 'problem solving', 'problem-solving',
    'time management', 'multitasking', 'reliable', 'punctual',
    'hardworking', 'dedicated', 'motivated', 'attention to detail',
  ],
}

const EDUCATION_LEVELS = [
  { keywords: ['phd', 'doctorate'], level: 5, label: 'PhD / Doctorate' },
  { keywords: ['masters', "master's", 'msc', 'mcom', 'mba'], level: 4, label: "Master's" },
  { keywords: ['honours', 'hons'], level: 4, label: 'Honours' },
  { keywords: ['bachelor', 'btech', 'bsc', 'bcom', 'bachelors', 'degree'], level: 3, label: "Bachelor's Degree" },
  { keywords: ['national diploma', 'diploma'], level: 2, label: 'Diploma' },
  { keywords: ['higher certificate'], level: 2, label: 'Higher Certificate' },
  { keywords: ['certificate', 'national certificate'], level: 1, label: 'Certificate' },
  { keywords: ['matric', 'grade 12', 'senior certificate'], level: 0, label: 'Matric / Grade 12' },
]

function hasKeyword(text, keyword) {
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(?:^|[^a-z0-9])${escaped}(?:$|[^a-z0-9])`, 'i')
  return re.test(text)
}

const SA_LOCATIONS = [
  // Major cities
  'johannesburg', 'joburg', 'jozi', 'pretoria', 'tshwane',
  'cape town', 'durban', 'ethekwini', 'port elizabeth', 'gqeberha',
  'bloemfontein', 'east london', 'polokwane', 'pietermaritzburg',
  'nelspruit', 'mbombela', 'kimberley', 'rustenburg',
  // Limpopo towns
  'makhado', 'louis trichardt', 'thohoyandou', 'tzaneen',
  'musina', 'mokopane', 'modimolle', 'bela-bela',
  // Gauteng towns
  'soweto', 'midrand', 'centurion', 'sandton', 'kempton park',
  'benoni', 'boksburg', 'germiston', 'randburg', 'roodepoort',
  'vereeniging', 'vanderbijlpark', 'mamelodi', 'atteridgeville',
  // Provinces
  'limpopo', 'gauteng', 'western cape', 'kwazulu-natal', 'kzn',
  'mpumalanga', 'north west', 'free state', 'northern cape',
  'eastern cape',
]

const PROVINCE_CITIES = {
  limpopo: ['makhado', 'polokwane', 'thohoyandou', 'tzaneen', 'louis trichardt', 'musina', 'mokopane', 'modimolle', 'bela-bela', 'limpopo'],
  gauteng: ['pretoria', 'johannesburg', 'joburg', 'jozi', 'tshwane', 'soweto', 'midrand', 'centurion', 'sandton', 'kempton park', 'benoni', 'boksburg', 'germiston', 'randburg', 'roodepoort', 'mamelodi', 'atteridgeville', 'gauteng'],
  wc: ['cape town', 'western cape'],
  kzn: ['durban', 'ethekwini', 'pietermaritzburg', 'kwazulu-natal', 'kzn'],
  mp: ['nelspruit', 'mbombela', 'mpumalanga'],
  nw: ['rustenburg', 'north west'],
  fs: ['bloemfontein', 'free state'],
  nc: ['kimberley', 'northern cape'],
  ec: ['port elizabeth', 'gqeberha', 'east london', 'eastern cape'],
}

function provinceOf(cityLower) {
  for (const [prov, cities] of Object.entries(PROVINCE_CITIES)) {
    if (cities.some(c => cityLower.includes(c))) return prov
  }
  return null
}

// ─── 2. PDF extraction ────────────────────────────────────────────

export async function extractCVText(file, onProgress) {
  if (!file) throw new Error('No file provided')
  if (typeof file.size === 'number' && file.size > MAX_CV_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1)
    throw new Error(`CV too large (${mb} MB, max 5 MB)`)
  }
  const lower = String(file.name || '').toLowerCase()
  if (!lower.endsWith('.pdf')) {
    throw new Error('Only PDF CVs are supported')
  }
  return extractPdfText(file, onProgress)
}

// ─── 3. Extractors ────────────────────────────────────────────────

/** Extract first name, last name, full name. Looks at the first ~10
 *  non-empty lines and picks the first that looks like a name
 *  (2-4 word tokens, all letters/hyphens/apostrophes, not a CV header).
 *  Skips lines that contain "curriculum vitae", "resume", etc. */
export function extractName(text) {
  const lines = String(text || '').split('\n').map(l => l.trim()).filter(Boolean)
  const disallow = /curriculum|resume|^cv\b|profile|personal\s+details|contact/i
  for (const line of lines.slice(0, 12)) {
    if (disallow.test(line)) continue
    const tokens = line.split(/\s+/).filter(Boolean)
    if (tokens.length < 2 || tokens.length > 4) continue
    if (!tokens.every(t => /^[A-Za-z][A-Za-z\-']{0,}$/.test(t))) continue
    // Skip lines that are likely headings (ALL CAPS short phrases)
    if (tokens.every(t => t === t.toUpperCase()) && line.length < 8) continue
    const firstName = tokens[0]
    const lastName = tokens[tokens.length - 1]
    return {
      firstName, lastName,
      fullName: `${firstName} ${lastName}`.trim(),
    }
  }
  return { firstName: '', lastName: '', fullName: '' }
}

/** Extract email + SA phone number. Returns `{email, phone}`. */
export function extractContacts(text) {
  const t = String(text || '')
  const emailMatch = t.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/)
  // SA mobile: +27 followed by 6/7/8 + 8 digits, OR 0 + 6/7/8 + 8 digits.
  // Tolerant to spaces, dashes, and parentheses.
  const phoneRe = /(?:\+27|27|0)[\s\-().]?[6-8][0-9][\s\-().]?[0-9]{3}[\s\-().]?[0-9]{4}/
  const phoneMatch = t.match(phoneRe)
  return {
    email: emailMatch ? emailMatch[0] : '',
    phone: phoneMatch ? phoneMatch[0].replace(/\s+/g, ' ').trim() : '',
  }
}

/** Find the first SA location keyword present in the text. */
export function extractLocation(text) {
  const lower = String(text || '').toLowerCase()
  for (const loc of SA_LOCATIONS) {
    if (lower.includes(loc)) {
      // Capitalise first letter of each word for display
      return loc.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    }
  }
  return ''
}

const MONTHS = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3,
  apr: 4, april: 4, may: 5, jun: 6, june: 6, jul: 7, july: 7,
  aug: 8, august: 8, sep: 9, sept: 9, september: 9,
  oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
}

/** Extract age and (optional) DOB. Recognises:
 *    "Age: 28", "28 years old", "Age 28"
 *    "DOB: 1995/03/15", "Date of Birth: 15 March 1995", "DOB 15-03-1995" */
export function extractAge(text) {
  const t = String(text || '')
  const thisYear = new Date().getFullYear()

  // Direct age mention
  const ageRe = /(?:age[:\s]+|^|[^0-9])(\d{2})\s*(?:years?\s*old|yrs?\s*old)?/i
  const ageOlderRe = /(\d{2})\s*(?:years?\s*old|yrs?\s*old)/i
  const explicitAge = t.match(/age[:\s]+(\d{2})\b/i) || t.match(ageOlderRe)
  let age = null
  if (explicitAge) {
    const a = parseInt(explicitAge[1], 10)
    if (a >= 16 && a <= 80) age = a
  }

  // DOB in various formats
  let dob = null
  // YYYY/MM/DD or YYYY-MM-DD
  let m = t.match(/(?:DOB|Date of Birth|Born)[^\n]*?(\d{4})[\-\/](\d{1,2})[\-\/](\d{1,2})/i)
  if (m) {
    const y = parseInt(m[1], 10)
    if (y >= 1940 && y <= thisYear - 15) {
      dob = `${m[1]}-${String(m[2]).padStart(2, '0')}-${String(m[3]).padStart(2, '0')}`
      if (age == null) age = thisYear - y
    }
  }
  // DD/MM/YYYY or DD-MM-YYYY
  if (!dob) {
    m = t.match(/(?:DOB|Date of Birth|Born)[^\n]*?(\d{1,2})[\-\/](\d{1,2})[\-\/](\d{4})/i)
    if (m) {
      const y = parseInt(m[3], 10)
      if (y >= 1940 && y <= thisYear - 15) {
        dob = `${m[3]}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`
        if (age == null) age = thisYear - y
      }
    }
  }
  // "15 March 1995"
  if (!dob) {
    m = t.match(/(?:DOB|Date of Birth|Born)[^\n]*?(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/i)
    if (m) {
      const month = MONTHS[m[2].toLowerCase()]
      const y = parseInt(m[3], 10)
      if (month && y >= 1940 && y <= thisYear - 15) {
        dob = `${m[3]}-${String(month).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`
        if (age == null) age = thisYear - y
      }
    }
  }

  return { age, dateOfBirth: dob }
}

/** Detect skills against SKILLS_DICTIONARY. Returns an array of
 *  `{ skill, category }` objects, each unique. Also extracts
 *  languages and certifications as separate flat lists for
 *  backward compatibility with the UI. */
export function detectSkills(text) {
  const lower = String(text || '').toLowerCase()
  const seen = new Set()
  const skills = []
  const languages = []
  const certifications = []

  for (const [category, terms] of Object.entries(SKILLS_DICTIONARY)) {
    for (const term of terms) {
      const t = term.toLowerCase()
      if (seen.has(t)) continue
      // Word-boundary-ish check: the term must appear surrounded by
      // non-word characters (or string boundaries) to avoid matching
      // "cook" inside "bookie". Cheaper than building a full \b regex
      // for each term.
      const idx = lower.indexOf(t)
      if (idx < 0) continue
      const before = idx === 0 ? ' ' : lower[idx - 1]
      const after = idx + t.length >= lower.length ? ' ' : lower[idx + t.length]
      if (/[a-z0-9]/.test(before) || /[a-z0-9]/.test(after)) continue
      seen.add(t)
      skills.push({ skill: term, category })
      if (category === 'languages') languages.push(term)
    }
  }

  // Common certifications — looked up by keyword
  const certTerms = [
    'first aid', 'food handler', "driver's licence", 'drivers licence',
    'forklift', 'fire marshal', 'occupational health',
  ]
  for (const c of certTerms) {
    if (lower.includes(c)) certifications.push(c)
  }

  return { skills, languages, certifications }
}

/** Count years of experience from date ranges like
 *  "2019-2022", "Jan 2019 - Dec 2022", "2020 - Present". Caps
 *  at 50 years total to guard against runaway duplicates. */
export function extractExperience(text) {
  const t = String(text || '')
  const rangePattern = /(\d{4})\s*(?:[-–—to]{1,3}|until)\s*(\d{4}|present|current|now)/gi
  const experiences = []
  let totalYears = 0
  const nowYear = new Date().getFullYear()

  const matches = Array.from(t.matchAll(rangePattern))
  for (const m of matches) {
    const startYear = parseInt(m[1], 10)
    const endRaw = String(m[2] || '').toLowerCase()
    const endYear = /present|current|now/.test(endRaw) ? nowYear : parseInt(endRaw, 10)
    if (!Number.isFinite(startYear) || !Number.isFinite(endYear)) continue
    if (startYear < 1960 || endYear > nowYear + 1) continue
    const years = Math.max(0, endYear - startYear)
    if (years > 50) continue
    totalYears += years
    experiences.push({
      company: '',      // filled below if we can find it on the same line
      role: '',
      duration: m[0],
      years,
      responsibilities: [],
    })
  }

  // Try to attach a role/company to each experience by walking back
  // over the nearest preceding non-empty line.
  if (experiences.length > 0) {
    const lines = t.split('\n').map(l => l.trim())
    for (let i = 0; i < lines.length && experiences.length; i++) {
      if (!rangePattern.test(lines[i])) continue
      // rangePattern has /g — reset
      rangePattern.lastIndex = 0
      if (experiences.length === 0) break
      const next = experiences.shift()
      const prev = lines.slice(Math.max(0, i - 2), i).filter(Boolean).pop() || ''
      next.role = prev
      experiences.unshift(next) // put back at front for the next iteration — naive but bounded
      break // one pass is enough for the attachment heuristic
    }
    rangePattern.lastIndex = 0
  }

  totalYears = Math.min(totalYears, 50)
  return { totalYears, experiences }
}

/** Detect highest education level present in the CV. Returns
 *  `{ level, label, institution, year, all }`. */
export function extractEducation(text) {
  const lower = String(text || '').toLowerCase()
  let best = { level: -1, label: '', match: '' }
  const all = []

  for (const tier of EDUCATION_LEVELS) {
    for (const kw of tier.keywords) {
      // Word-boundary match — avoids "diploma" matching "ma" (masters)
      // or "mba" matching inside longer words.
      if (hasKeyword(lower, kw)) {
        all.push({ qualification: tier.label, field: '', institution: '', year: null })
        if (tier.level > best.level) {
          best = { level: tier.level, label: tier.label, match: kw }
        }
        break
      }
    }
  }

  // Institution hint — line containing "college", "university", "tvet", "school"
  let institution = ''
  let year = null
  const lines = String(text || '').split('\n').map(l => l.trim()).filter(Boolean)
  for (const line of lines) {
    if (!institution && /college|university|tvet|polytechnic|academy/i.test(line) && line.length < 80) {
      institution = line
    }
    if (year == null) {
      const y = line.match(/\b(19|20)\d{2}\b/)
      if (y && /matric|grade 12|diploma|degree|certificate|graduated|qualified/i.test(line)) {
        year = parseInt(y[0], 10)
      }
    }
  }

  return {
    level: best.level < 0 ? 0 : best.level,
    label: best.label,
    institution,
    year,
    all: all.length > 0 ? all : [{ qualification: best.label || '', field: '', institution, year }],
  }
}

// ─── 4. analyseCV — orchestrator ──────────────────────────────────

/** Run all extractors against a CV text and return the structured
 *  candidate data shape (same shape the UI expects). */
export function analyseCV(cvText /* , jobOpening is unused locally */) {
  const { firstName, lastName, fullName } = extractName(cvText)
  const { email, phone } = extractContacts(cvText)
  const location = extractLocation(cvText)
  const { age, dateOfBirth } = extractAge(cvText)
  const { skills, languages, certifications } = detectSkills(cvText)
  const { totalYears, experiences } = extractExperience(cvText)
  const edu = extractEducation(cvText)

  return {
    firstName,
    lastName,
    fullName,
    email,
    phone,
    location,
    age,
    dateOfBirth,
    skills: skills.map(s => s.skill),
    _skillsWithCategory: skills, // kept for scoring — scored via category
    languages,
    certifications,
    experience: experiences,
    totalExperienceYears: totalYears,
    education: edu.all,
    highestQualification: edu.label,
    educationLevel: edu.level,
    extractionNotes: '',
  }
}

// ─── 5. Rule-based scoring ────────────────────────────────────────

const EDU_POINTS = [8, 12, 16, 18, 20] // matric=8, cert=12, diploma=16, degree=18, honours+=20 (clamped)

function normalise(s) { return String(s || '').toLowerCase().trim() }

export function scoreCandidate(candidate, jobOpening, branch) {
  // Skills (30)
  const required = (jobOpening?.requiredSkills || []).map(normalise).filter(Boolean)
  const niceToHave = (jobOpening?.niceToHaveSkills || []).map(normalise).filter(Boolean)
  const candidateSkills = (candidate?.skills || []).map(normalise)

  let skillsScore = 0
  if (required.length > 0) {
    const matched = required.filter(rs =>
      candidateSkills.some(cs => cs.includes(rs) || rs.includes(cs))
    )
    skillsScore = Math.round((matched.length / required.length) * 30)
    const bonus = niceToHave.filter(ns =>
      candidateSkills.some(cs => cs.includes(ns) || ns.includes(cs))
    ).length
    skillsScore = Math.min(30, skillsScore + Math.min(bonus, 3))
  } else {
    skillsScore = Math.min(candidateSkills.length * 3, 30)
  }

  // Experience (25)
  const years = Number(candidate?.totalExperienceYears) || 0
  let experienceScore
  if (years >= 10) experienceScore = 25
  else if (years >= 5) experienceScore = 22
  else if (years >= 3) experienceScore = 18
  else if (years >= 1) experienceScore = 12
  else if (years > 0) experienceScore = 8
  else experienceScore = 5

  // Education (20)
  const educationScore = EDU_POINTS[Math.min(Math.max(0, Number(candidate?.educationLevel) || 0), EDU_POINTS.length - 1)]

  // Age (10)
  let ageScore = 5
  const age = Number(candidate?.age) || null
  const min = Number(jobOpening?.preferredAgeMin) || null
  const max = Number(jobOpening?.preferredAgeMax) || null
  if (age && min && max) {
    if (age >= min && age <= max) ageScore = 10
    else if (age >= min - 5 && age <= max + 5) ageScore = 6
    else ageScore = 2
  }

  // Location (10)
  let locationScore = 5
  const candLoc = normalise(candidate?.location)
  const prefLoc = normalise(jobOpening?.preferredLocation)
  if (candLoc && prefLoc) {
    if (candLoc.includes(prefLoc) || prefLoc.includes(candLoc)) {
      locationScore = 10
    } else {
      const pCand = provinceOf(candLoc)
      const pPref = provinceOf(prefLoc)
      if (pCand && pPref && pCand === pPref) locationScore = 7
      else locationScore = 3
    }
  }

  // Languages (5)
  const localLangsForBranch = (BRANCH_LOCAL_LANGUAGES[normalise(branch)] || []).map(normalise)
  const langsLower = (candidate?.languages || []).map(normalise)
  const hasEnglish = langsLower.includes('english')
  const hasLocal = localLangsForBranch.some(l => langsLower.includes(l))
  let languageScore = 2
  if (hasEnglish && hasLocal) languageScore = 5
  else if (hasEnglish || hasLocal) languageScore = 3
  else if (langsLower.length > 0) languageScore = 2
  else languageScore = 2

  const rankingScore = skillsScore + experienceScore + educationScore + ageScore + locationScore + languageScore

  let recommendation
  if (rankingScore >= 80) recommendation = 'Strongly Recommend'
  else if (rankingScore >= 65) recommendation = 'Recommend'
  else if (rankingScore >= 45) recommendation = 'Maybe'
  else recommendation = 'Do Not Recommend'

  return {
    rankingScore,
    rankingBreakdown: {
      skillsScore, experienceScore, educationScore,
      ageScore, locationScore, languageScore,
    },
    rankingReason: generateRankingReason(candidate, jobOpening, { skillsScore, experienceScore, years }),
    strengths: generateStrengths(candidate, jobOpening, branch),
    weaknesses: generateWeaknesses(candidate, jobOpening),
    recommendation,
  }
}

function generateRankingReason(candidate, jobOpening, { skillsScore, experienceScore, years }) {
  const parts = []
  if (skillsScore >= 24) parts.push('strong skills match')
  else if (skillsScore >= 15) parts.push('partial skills match')
  else parts.push('limited skills match')

  if (years >= 5) parts.push(`${years} years of experience`)
  else if (years >= 1) parts.push(`${years} year${years === 1 ? '' : 's'} of experience`)
  else parts.push('limited experience')

  if (experienceScore >= 22) parts.push('well-suited on seniority')

  return `Candidate shows ${parts.join(', ')}.`
}

function generateStrengths(candidate, jobOpening, branch) {
  const out = []
  const years = Number(candidate?.totalExperienceYears) || 0
  if (years >= 5) out.push(`${years}+ years of relevant experience`)
  if ((candidate?.educationLevel || 0) >= 2) out.push(`Holds a ${candidate.highestQualification || 'formal qualification'}`)
  const skillsLower = (candidate?.skills || []).map(normalise)
  if (skillsLower.some(s => s.includes('food') || s.includes('cook') || s.includes('kitchen'))) {
    out.push('Food service background')
  }
  const localLangs = (BRANCH_LOCAL_LANGUAGES[normalise(branch)] || []).map(normalise)
  const langsLower = (candidate?.languages || []).map(normalise)
  if (localLangs.some(l => langsLower.includes(l))) {
    out.push('Speaks a local language for this branch')
  }
  if ((candidate?.certifications || []).length > 0) {
    out.push(`${candidate.certifications.length} relevant certification${candidate.certifications.length === 1 ? '' : 's'}`)
  }
  return out.slice(0, 4)
}

function generateWeaknesses(candidate, jobOpening) {
  const out = []
  const required = (jobOpening?.requiredSkills || []).map(normalise)
  const candidateSkills = (candidate?.skills || []).map(normalise)
  const missing = required.filter(rs => !candidateSkills.some(cs => cs.includes(rs) || rs.includes(cs)))
  if (missing.length > 0) {
    out.push(`Missing required skills: ${missing.slice(0, 3).join(', ')}`)
  }
  if (!candidate?.email) out.push('No email found in CV')
  if (!candidate?.phone) out.push('No phone number found in CV')
  const years = Number(candidate?.totalExperienceYears) || 0
  if (years < 1) out.push('No measurable work experience detected')
  if ((candidate?.educationLevel || 0) === 0 && !candidate?.highestQualification) {
    out.push('No formal qualification detected')
  }
  return out.slice(0, 3)
}

// ─── 6. Job requirement extraction (local) ────────────────────────

/** Derive required skills from a typed or uploaded JD. Pure
 *  keyword-matching on the same SKILLS_DICTIONARY. Also detects a
 *  "min experience" hint and tries to guess department + title. */
export function extractJobRequirements(jobDescriptionText) {
  const text = String(jobDescriptionText || '')
  const lower = text.toLowerCase()
  const { skills } = detectSkills(text)
  const requiredSkills = skills
    .filter(s => s.category !== 'languages' && s.category !== 'qualifications')
    .map(s => s.skill)

  // Min experience — "X years experience" / "X+ years"
  let minExperience = 0
  const yrsMatch = lower.match(/(\d+)\s*\+?\s*(?:years?|yrs?)\s*(?:of\s*)?(?:experience|work)/)
  if (yrsMatch) minExperience = Math.min(40, parseInt(yrsMatch[1], 10) || 0)

  // Min education — highest education keyword present
  const eduBest = extractEducation(text)

  // Suggested department
  let suggestedDepartment = 'Other'
  if (/kitchen|chef|cook/i.test(text)) suggestedDepartment = 'Kitchen'
  else if (/baker|bakery|confection|pastry/i.test(text)) suggestedDepartment = 'Bakery'
  else if (/tuck\s*shop|till|cashier/i.test(text)) suggestedDepartment = 'Tuck Shop'
  else if (/admin|office|bookkeep|payroll|reception/i.test(text)) suggestedDepartment = 'Admin'

  // Suggested title — first non-empty line that looks like a title
  let suggestedJobTitle = ''
  for (const line of text.split('\n').map(l => l.trim()).filter(Boolean).slice(0, 5)) {
    if (line.length >= 3 && line.length <= 60 && /[A-Za-z]/.test(line) && !line.includes(':')) {
      suggestedJobTitle = line
      break
    }
  }

  return {
    suggestedJobTitle,
    suggestedDepartment,
    requiredSkills: requiredSkills.slice(0, 10),
    niceToHaveSkills: [],
    minExperience,
    minEducation: eduBest.label,
    preferredAgeMin: null,
    preferredAgeMax: null,
    preferredLocation: '',
    otherRequirements: [],
  }
}

// ─── 7. Batch orchestrator ────────────────────────────────────────

function sleep(ms) { return new Promise(resolve => setTimeout(resolve, ms)) }

/** Process files end-to-end: extract → analyse → score → save.
 *  No network calls other than the local PDF worker. */
export async function processCVBatch(files, jobOpening, branch, onProgress, { delayMs = DEFAULT_BATCH_DELAY_MS } = {}) {
  const list = Array.from(files || []).slice(0, MAX_BATCH_SIZE)
  const total = list.length
  const candidates = []
  let processed = 0
  let failed = 0
  let duplicates = 0

  const notify = (update) => {
    try { onProgress?.({ total, ...update }) } catch { /* swallow */ }
  }

  for (let i = 0; i < list.length; i++) {
    const file = list[i]
    const current = i + 1
    const fileName = file.name
    notify({ current, fileName, status: 'processing', candidate: null, step: 'extracting' })

    // 1. Extract PDF text
    let cvText = ''
    try {
      cvText = await extractCVText(file, (msg) => {
        notify({ current, fileName, status: 'processing', candidate: null, step: msg })
      })
    } catch (err) {
      failed++
      notify({ current, fileName, status: 'failed', error: err?.message || 'PDF extraction failed', candidate: null })
      Sentry.captureException(err, { extra: { fn: 'processCVBatch.extractCVText', fileName, branch, jobOpeningId: jobOpening.id } })
      if (i < list.length - 1) await sleep(delayMs)
      continue
    }

    // 2. Analyse locally
    notify({ current, fileName, status: 'processing', candidate: null, step: 'analysing' })
    const extracted = analyseCV(cvText)
    const fullName = extracted.fullName || fileName.replace(/\.pdf$/i, '')
    const email = extracted.email || ''

    // 3. Duplicate check
    const existing = findExistingCandidate(branch, jobOpening.id, fullName, email)
    if (existing) {
      duplicates++
      notify({ current, fileName, status: 'duplicate', candidate: existing, step: 'duplicate' })
      if (i < list.length - 1) await sleep(delayMs)
      continue
    }

    // 4. Score locally
    notify({ current, fileName, status: 'processing', candidate: null, step: 'scoring' })
    const score = scoreCandidate(extracted, jobOpening, branch)

    // 5. Save
    try {
      const saved = await saveCandidate({
        branch,
        jobOpeningId: jobOpening.id,
        ...extracted,
        fullName,
        cvText,
        cvFileName: fileName,
        rankingScore: score.rankingScore,
        rankingBreakdown: score.rankingBreakdown,
        rankingReason: score.rankingReason,
        strengths: score.strengths,
        weaknesses: score.weaknesses,
        recommendation: score.recommendation,
        status: 'pending',
      })
      processed++
      candidates.push(saved)
      notify({ current, fileName, status: 'done', candidate: saved })
    } catch (err) {
      failed++
      notify({ current, fileName, status: 'failed', error: err?.message || 'Save failed', candidate: null })
      // saveCandidate's restSetSafe already captured the REST error;
      // this breadcrumb records the batch context so upstream sessions
      // can be correlated with individual file failures.
      Sentry.addBreadcrumb({
        category: 'recruitment',
        message: `processCVBatch save failed: ${fileName}`,
        data: { fileName, branch, jobOpeningId: jobOpening.id },
        level: 'warning',
      })
    }

    if (i < list.length - 1) await sleep(delayMs)
  }

  return { processed, failed, duplicates, candidates, total }
}

/** Re-run scoring (not extraction) for every candidate on an
 *  opening, using their stored extracted fields. Useful when the
 *  job's required skills or preferred age change. */
export async function rescoreAllCandidates(jobOpening, branch, onProgress, { delayMs = 50 } = {}) {
  if (!jobOpening?.id) throw new Error('jobOpening.id is required')
  const list = getCandidates(branch, { jobOpeningId: jobOpening.id })
  const total = list.length
  let rescored = 0
  let failed = 0

  const notify = (update) => {
    try { onProgress?.({ total, ...update }) } catch { /* swallow */ }
  }

  for (let i = 0; i < list.length; i++) {
    const cand = list[i]
    const current = i + 1
    notify({ current, fileName: cand.fullName, status: 'processing' })

    try {
      const score = scoreCandidate(cand, jobOpening, branch)
      await updateCandidate(cand.id, branch, {
        rankingScore: score.rankingScore,
        rankingBreakdown: score.rankingBreakdown,
        rankingReason: score.rankingReason,
        strengths: score.strengths,
        weaknesses: score.weaknesses,
        recommendation: score.recommendation,
        analysisFailed: false,
        analysisError: '',
      })
      rescored++
      notify({ current, fileName: cand.fullName, status: 'done' })
    } catch (err) {
      failed++
      notify({ current, fileName: cand.fullName, status: 'failed', error: err?.message || 'Re-score failed' })
      Sentry.captureException(err, { extra: { fn: 'rescoreAllCandidates', candidateId: cand.id, branch, jobOpeningId: jobOpening.id } })
    }

    if (i < list.length - 1) await sleep(delayMs)
  }

  return { rescored, failed, total }
}
